import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { useEffect, useMemo, useState } from 'react';
import { OneLineText } from '@/components/OneLineText';
import { ColumnDef } from '@tanstack/react-table';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { DDI_RUSH_FILTER_OPTIONS, DDI_WORK_ORDER_STATUS_OPTIONS, Feature } from '@/utils/constant';
import { useFilterStore } from '@/stores/filterStore';
import { AdvanceFilter } from '@/types/types';
import { hasFeature } from '@/services/featureService';
import { isAdminOrSupervisor, isDueDiligenceTeamMember } from '@/services/userService';
import { useUserStore } from '@/stores/user';
import { cn, convertISODateToMMDDYYYY, formatBillingCurrency, isNegativeBillingAmount } from '@/lib/utils';
import { displayError } from '@/lib/error';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { toast } from '@/components/ui/use-toast';
import {
  ddiFiltersToParams,
  useDDIAllCaseSubtypeOptions,
  useDDIAllSubtypeOptions,
  useDDICaseTypes,
  useDDITypes,
  useDDIWorkOrders,
  useUpdateDdiWorkOrderStatus,
} from '@/services/dueDiligenceService';
import type {
  DdiWorkOrderGetAllRequest,
  DdiWorkOrderGetAllResponse,
  DdiWorkOrderDetailResponse,
  DdiWorkOrderListItem,
  DdiWorkOrderStatus,
} from '@/types/dueDiligence.types';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Flag } from 'lucide-react';
import { DDIAdvanceFilters } from './DDIAdvanceFilters';
import DDIWorkOrderSidePanel from './DDIWorkOrderSidePanel';
import DDICommentsDialog from './DDICommentsDialog';
import { DDIRowActions } from './DDIRowActions';
import {
  buildDdiSearchFilters,
  DDI_DEFAULT_SEARCH_FIELD_NAMES,
} from './ddiSearchConfig';

// Colors per REQ-9 (docs/specs/ddi-work-order-status-update/requirements.md) — must match
// the DDI work order status pill spec from issue #431 exactly.
const statusClassMap: Record<DdiWorkOrderStatus, string> = {
  OPEN: 'bg-[#C0CBD9] text-[#0A2240] font-medium hover:bg-[#C0CBD9]',
  PENDING_RESULTS: 'bg-[#FFEEBF] text-[#6C4A09] font-medium hover:bg-[#FFEEBF]',
  ASSIGNED: 'bg-[#E1EAFB] text-[#0A2240] font-medium hover:bg-[#E1EAFB]',
  AWAITING_CLIENT: 'bg-[#FBEDFF] text-[#551175] font-medium hover:bg-[#FBEDFF]',
  REVIEW_AND_BILL: 'bg-[#551175] text-[#F5F5F5] font-medium hover:bg-[#551175]',
  BILLED: 'bg-[#D5F4E3] text-[#1B8652] font-medium hover:bg-[#D5F4E3]',
  CANCELED: 'bg-[#F5F5F5] text-[#969696] font-medium hover:bg-[#F5F5F5]',
};

export const DDIStatusPill = ({ status }: { status: string }) => {
  const label = DDI_WORK_ORDER_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
  const className = statusClassMap[status as DdiWorkOrderStatus] ?? 'bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5]';

  return (
    <div className={`h-[20px] w-fit px-3 text-xs font-medium mr-5 rounded-full flex items-center justify-center ${className}`}>
      {label}
    </div>
  );
};

const DDIList = () => {
  const navigate = useNavigate();

  const filterStore = useFilterStore((state) => state);
  const { ddiFilters, setDDIFilters } = filterStore;

  const methods = useForm();
  const queryClient = useQueryClient();
  const loggedInUser = useUserStore((state) => state.user);
  const statusUpdateMutation = useUpdateDdiWorkOrderStatus();

  const canUpdateStatusAtAll =
    (isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser));

  const canCreate =
    hasFeature(Feature.DUE_DILIGENCE) &&
    hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_CREATE) &&
    (isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser));

  // Per requirements.md REQ-3: only DDI-team members, supervisors, or admins may view the
  // work order detail panel. The backend only flag-gates this endpoint (no team/role check),
  // so this restriction is enforced client-side.
  const canViewPanel =
    hasFeature(Feature.DUE_DILIGENCE) &&
    hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_READ) &&
    (isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser));

  // Search UI requires parent + read (REQ-1). List page itself stays on READ only.
  const canAccessSearch =
    hasFeature(Feature.DUE_DILIGENCE) && hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_READ);

  // When search is gated off, drop persisted filters so the list stays unfiltered and stale
  // criteria do not reappear if the parent flag is turned back on later.
  useEffect(() => {
    if (!canAccessSearch && ddiFilters.length > 0) {
      setDDIFilters([]);
    }
  }, [canAccessSearch, ddiFilters.length, setDDIFilters]);

  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(null);
  const [commentWorkOrderId, setCommentWorkOrderId] = useState<string | null>(null);
  const [openCommentPanel, setOpenCommentPanel] = useState(false);
  const [commentCountMap, setCommentCountMap] = useState<Record<string, number>>({});
  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);
  const [listQueryState, setListQueryState] = useState<{ isError: boolean; refetch: () => void }>({
    isError: false,
    refetch: () => undefined,
  });

  const handleOpenComments = (workOrderId: string) => {
    setCommentWorkOrderId(workOrderId);
    setOpenCommentPanel(true);
  };

  const handleCloseComments = () => {
    setOpenCommentPanel(false);
    setCommentWorkOrderId(null);
  };

  const handleCountUpdate = (workOrderId: string, count: number) => {
    setCommentCountMap((prev) => ({ ...prev, [workOrderId]: count }));
  };

  const { data: ddiTypesData } = useDDITypes();
  const { data: caseTypesData } = useDDICaseTypes({ enabled: isAdvanceFilterOpen });
  const { options: ddiSubtypeOptions } = useDDIAllSubtypeOptions({ enabled: isAdvanceFilterOpen });
  const { options: caseSubtypeOptions } = useDDIAllCaseSubtypeOptions({ enabled: isAdvanceFilterOpen });

  const ddiTypeOptions = useMemo(
    () => (ddiTypesData ?? []).map((t) => ({ label: t.name, value: t.name })),
    [ddiTypesData]
  );
  const caseTypeOptions = useMemo(
    () => (caseTypesData ?? []).map((t) => ({ label: t.name, value: t.name })),
    [caseTypesData]
  );

  const ddiSearchFields: DataTableSearchConfig[] = useMemo(
    () => [
      { label: 'Work Order Number', name: 'workOrderNumber', placeholder: 'Search Work Order Number' },
      { label: 'Client ID', name: 'clientCode', placeholder: 'Search Client ID' },
      { label: 'Case Number', name: 'caseNumber', placeholder: 'Search Case Number' },
      { label: 'Assigned User', name: 'assignedUser', placeholder: 'Search Assigned User' },
      {
        label: 'Status',
        name: 'status',
        placeholder: 'Select Status',
        fieldType: 'SELECT',
        options: DDI_WORK_ORDER_STATUS_OPTIONS,
      },
      {
        label: 'DDI Type',
        name: 'ddiType',
        placeholder: 'Select DDI Type',
        fieldType: 'SELECT',
        options: ddiTypeOptions,
      },
    ],
    [ddiTypeOptions]
  );

  const getDDIWorkOrdersData = (
    params: DdiWorkOrderGetAllRequest & { filters?: AdvanceFilter[] },
    options?: { query?: UseQueryOptions<DdiWorkOrderGetAllResponse> }
  ) => {
    const { filters, pageNo, pageSize, sortField, sortDirection, rushType } = params;
    const apiParams: DdiWorkOrderGetAllRequest = {
      pageNo,
      pageSize,
      sortField: sortField ?? 'entryDate',
      sortDirection: sortDirection ?? 'desc',
      rushType: rushType ?? undefined,
      filters: ddiFiltersToParams(filters ?? []),
    };

    const query = useDDIWorkOrders(apiParams, options);

    useEffect(() => {
      setListQueryState({ isError: query.isError, refetch: query.refetch });
    }, [query.isError, query.refetch]);

    const data = query.data?.data ?? [];
    const totalRows = query.data?.pagination?.totalCount ?? data.length;

    return { ...query, data, totalRows };
  };

  const columns: ColumnDef<DdiWorkOrderListItem>[] = [
    {
      accessorKey: 'workOrderNumber',
      header: 'Invoice #',
      enableSorting: true,
      size: 130,
      cell: ({ row }) => (
        <div className={`${row.original.isRush ? 'ml-[-10px]' : 'pl-1'} flex flex-row gap-1 items-center min-w-[110px] mr-5`}>
          {row.original.isRush && <Flag className="text-[#D72222] w-3 h-3 shrink-0" fill="#D72222" />}
          <OneLineText text={row.original.workOrderNumber} />
        </div>
      ),
    },
    {
      accessorKey: 'clientCode',
      header: 'Client #',
      cell: ({ row }) => <OneLineText text={row.original.clientCode} className="min-w-[90px] mr-5" />,
    },
    {
      accessorKey: 'entryDate',
      header: 'Date Created',
      enableSorting: true,
      cell: ({ row }) => (
        <OneLineText text={convertISODateToMMDDYYYY(row.original.entryDate)} className="min-w-[100px] mr-5" />
      ),
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      enableSorting: true,
      cell: ({ row }) => (
        <OneLineText text={convertISODateToMMDDYYYY(row.original.dueDate)} className="min-w-[100px] mr-5" />
      ),
    },
    {
      accessorKey: 'ddiType',
      header: 'DDI Type',
      enableSorting: true,
      cell: ({ row }) => <OneLineText text={row.original.ddiType} className="min-w-[140px] mr-5" />,
    },
    {
      accessorKey: 'assignedUser',
      header: 'Assigned User',
      cell: ({ row }) => (
        <OneLineText text={row.original.assignedUser?.name ?? '-'} className="min-w-[120px] mr-5" />
      ),
    },
    {
      accessorKey: 'fees',
      header: 'Fees',
      cell: ({ row }) => (
        <OneLineText
          text={formatBillingCurrency(row.original.fees)}
          className={cn('min-w-[80px] mr-5', isNegativeBillingAmount(row.original.fees) && 'text-destructive')}
        />
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: true,
      size: 160,
      cell: ({ row }) => {
        const previousStatus = row.original.status;
        const canEdit = canUpdateStatusAtAll;

        if (!canEdit) {
          return (
            <div
              className={`h-[20px] w-[140px] text-xs font-medium mr-5 rounded-full flex items-center justify-center ${
                statusClassMap[previousStatus] ?? 'bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5]'
              }`}
            >
              {DDI_WORK_ORDER_STATUS_OPTIONS.find((o) => o.value === previousStatus)?.label ?? previousStatus}
            </div>
          );
        }

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <FormFieldSelect
              name={`ddi-status-${row.original.id}`}
              hasControl={false}
              isLoading={
                statusUpdateMutation.status === 'pending' &&
                row.original.id === statusUpdateMutation.variables?.workOrderId
              }
              options={DDI_WORK_ORDER_STATUS_OPTIONS}
              className={`h-[20px] w-[140px] text-xs font-medium mr-5 rounded-full shadow-none border-0 ${
                statusClassMap[row.original.status] ?? 'bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5]'
              }`}
              defaultValue={row.original.status}
              onChange={(value) => {
                if (!value) return;
                statusUpdateMutation.mutate(
                  { workOrderId: row.original.id, status: value as DdiWorkOrderStatus },
                  {
                    onSuccess: async (data) => {
                      queryClient.setQueriesData<DdiWorkOrderGetAllResponse>(
                        { queryKey: ['ddi-work-orders'] },
                        (old) => {
                          if (!old) return old;
                          return {
                            ...old,
                            data: old.data.map((wo) =>
                              wo.id === row.original.id ? { ...wo, status: data.status } : wo
                            ),
                          };
                        }
                      );
                      queryClient.setQueriesData<DdiWorkOrderDetailResponse>(
                        { queryKey: ['ddi-work-order-detail', row.original.id] },
                        (old) => (old ? { ...old, summary: { ...old.summary, status: data.status } } : old)
                      );
                      await Promise.all([
                        queryClient.invalidateQueries({ queryKey: ['ddi-work-orders'] }),
                        queryClient.invalidateQueries({ queryKey: ['ddi-work-order-detail', row.original.id] }),
                      ]);
                      toast({
                        variant: 'default',
                        description: 'Work order status updated successfully.',
                        duration: 3000,
                      });
                    },
                    onError: (error) => {
                      if (error?.error?.message === 'Unsupported status transition') {
                        const fromLabel =
                          DDI_WORK_ORDER_STATUS_OPTIONS.find((o) => o.value === previousStatus)?.label ??
                          previousStatus;
                        const toLabel =
                          DDI_WORK_ORDER_STATUS_OPTIONS.find((o) => o.value === value)?.label ?? value;
                        toast({
                          variant: 'destructive',
                          description: `Transition from ${fromLabel} to ${toLabel} is not supported.`,
                          duration: 5000,
                        });
                      } else {
                        displayError(error, 5000);
                      }
                    },
                  }
                );
              }}
            />
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      size: 140,
      cell: ({ row }) => (
        <DDIRowActions
          row={row.original}
          navigate={navigate}
          onOpenComments={handleOpenComments}
          commentCount={commentCountMap[row.original.id] ?? row.original.commentCount}
        />
      ),
    },
  ];

  // List itself stays on READ (route/sidebar parity).
  if (!hasFeature(Feature.DUE_DILIGENCE_WORK_ORDER_READ)) return null;

  return (
    <div className="flex w-[100%]">
      <FormProvider {...methods}>
        <div className="w-full flex flex-col gap-6">
          {listQueryState.isError && (
            <div className="flex items-center justify-between rounded-md border border-[#D72222] bg-[#FFF5F5] px-4 py-3 text-sm text-[#D72222]">
              <span>Failed to load Due Diligence work orders.</span>
              <button
                type="button"
                className="underline font-medium"
                onClick={() => listQueryState.refetch()}
              >
                Retry
              </button>
            </div>
          )}
          <DataTable
            columns={columns}
            apiGet={getDDIWorkOrdersData}
            onRowClick={(row, e) => {
              const target = e.target as HTMLElement;
              if (target.closest('.action')) return;
              if (canViewPanel) setSelectedWorkOrderId(row.id);
            }}
            toolbar={{
              toolBarTitle: 'Due Diligence Inquiry',
              addButton: canCreate,
              addButtonTitle: 'Add Entry',
              addOnClick: () => navigate('/due-diligence/create'),
              filters: {
                groups: [
                  {
                    name: 'rushType',
                    label: 'Rush',
                    options: DDI_RUSH_FILTER_OPTIONS,
                    defaultValue: null,
                  },
                ],
              },
            }}
            search={canAccessSearch ? ddiSearchFields : undefined}
            onSearch={(search) => {
              // Reset: clear all default + advanced filters. DataTable still re-queries with
              // empty filters (Process Service parity) — filters are preserved on API errors.
              if (!search) {
                setDDIFilters([]);
                return;
              }

              const defaultSearch: Record<string, string | undefined | null> = {};
              for (const name of DDI_DEFAULT_SEARCH_FIELD_NAMES) {
                defaultSearch[name] = (search[name] as string | undefined) ?? '';
              }
              // Include any other search keys (defensive)
              Object.entries(search).forEach(([field, value]) => {
                if (!(field in defaultSearch)) {
                  defaultSearch[field] = value as string;
                }
              });

              setDDIFilters(buildDdiSearchFilters(defaultSearch, ddiFilters, DDI_DEFAULT_SEARCH_FIELD_NAMES));
            }}
            advanceFilter={
              canAccessSearch
                ? {
                    filters: ddiFilters,
                    setIsAdvanceFilterOpen,
                  }
                : undefined
            }
          />
        </div>
      </FormProvider>
      {selectedWorkOrderId && (
        <DDIWorkOrderSidePanel workOrderId={selectedWorkOrderId} onClose={() => setSelectedWorkOrderId(null)} />
      )}
      {openCommentPanel && commentWorkOrderId && (
        <DDICommentsDialog
          workOrderId={commentWorkOrderId}
          open={openCommentPanel}
          onClose={handleCloseComments}
          onCountUpdate={handleCountUpdate}
        />
      )}
      <DDIAdvanceFilters
        open={isAdvanceFilterOpen}
        onClose={() => setIsAdvanceFilterOpen(false)}
        filters={ddiFilters}
        setFilters={setDDIFilters}
        ddiListViewFilters={ddiSearchFields
          .map((f) => f.name)
          // Keep clientCode in Advanced Search (issue #468). Default "Client ID" still uses the
          // same API field, but excluding it here prevents applied advanced rows from being stripped.
          .filter((name) => name !== 'clientCode')}
        ddiTypeOptions={ddiTypeOptions}
        ddiSubtypeOptions={ddiSubtypeOptions}
        caseTypeOptions={caseTypeOptions}
        caseSubtypeOptions={caseSubtypeOptions}
      />
    </div>
  );
};

export default DDIList;
